import type { GameFlowState, GameFlowStep } from './gameFlow';
import { GameRoomType } from '../utils/enums';

/** 配對視窗語意階段（與選股／挑戰賽 step 解耦） */
export type MatchPhase =
  | 'none'
  | 'searching'
  | 'countdown'
  | 'inviting'
  | 'invited'
  | 'creating_room'
  | 'entering_game';

/** 配對成功後的建房角色 */
export type MatchRole = 'host' | 'guest' | 'solo_bot';

export const BOT_OPPONENT_ID = -1;

export const MATCH_COUNTDOWN_SECONDS = 5;

/** 配對相關 Hub 事件（契約對照用） */
export type MatchmakingHubEvent =
  | 'ReceiveMatchID'
  | 'MatchFound'
  | 'OpponentLeft'
  | 'ReceiveGameRoomID'
  | 'ReceiveInviteID'
  | 'ReceiveFriendMatchInvite'
  | 'FriendMatchRejected'
  | 'CancelFriendMatch';

export type MatchmakingStoreAction =
  | 'onReceiveMatchID'
  | 'onMatchFound'
  | 'onOpponentLeft'
  | 'onReceiveGameRoomID'
  | 'onReceiveInviteID'
  | 'onReceiveFriendMatchInvite'
  | 'onFriendMatchRejected'
  | 'onCancelFriendMatch'
  | 'cancelMatch'
  | 'cancelFriendInvite';

/** Hub 事件 → store action → matchPhase（目標契約） */
export const MATCHMAKING_HUB_TRANSITIONS: ReadonlyArray<{
  event: MatchmakingHubEvent;
  action: MatchmakingStoreAction;
  nextPhase: MatchPhase | 'none + alert';
}> = [
  { event: 'ReceiveMatchID', action: 'onReceiveMatchID', nextPhase: 'searching' },
  { event: 'MatchFound', action: 'onMatchFound', nextPhase: 'countdown' },
  { event: 'OpponentLeft', action: 'onOpponentLeft', nextPhase: 'searching' },
  { event: 'ReceiveGameRoomID', action: 'onReceiveGameRoomID', nextPhase: 'entering_game' },
  { event: 'ReceiveInviteID', action: 'onReceiveInviteID', nextPhase: 'inviting' },
  { event: 'ReceiveFriendMatchInvite', action: 'onReceiveFriendMatchInvite', nextPhase: 'invited' },
  { event: 'FriendMatchRejected', action: 'onFriendMatchRejected', nextPhase: 'none + alert' },
  { event: 'CancelFriendMatch', action: 'onCancelFriendMatch', nextPhase: 'none' },
];

export function isMultiplayerMatchType(type: GameRoomType): boolean {
  return type === GameRoomType.MultiRandom || type === GameRoomType.MultiAssign;
}

export function deriveMatchRole(
  opponentID: number | undefined,
  createRoom: boolean | undefined,
): MatchRole | undefined {
  if (opponentID == null) return undefined;
  if (opponentID === BOT_OPPONENT_ID) return 'solo_bot';
  if (createRoom === true) return 'host';
  return 'guest';
}

export function deriveMatchPhaseFromState(
  state: Pick<GameFlowState, 'step' | 'type'>,
): MatchPhase {
  const { step, type } = state;

  switch (step) {
    case 'matching':
      return 'searching';
    case 'countdown':
      return 'countdown';
    case 'waitingFriendResponse':
    case 'waitingInviteID':
      return 'inviting';
    case 'answerFriendResponse':
    case 'acceptFriendInviteAssignStockBattleSelectStock':
      return 'invited';
    case 'sendRoomID':
      return 'creating_room';
    case 'playing':
      return isMultiplayerMatchType(type) ? 'entering_game' : 'none';
    default:
      return 'none';
  }
}

export function isActiveMatchPhase(phase: MatchPhase): boolean {
  return (
    phase !== 'none' &&
    phase !== 'entering_game' &&
    phase !== 'creating_room'
  );
}

/** 是否應 invoke JoinMatchQueue（已有 matchID 表示已在隊列中） */
export function shouldInvokeJoinMatchQueue(
  state: Pick<GameFlowState, 'step' | 'matchID' | 'type'>,
): boolean {
  if (state.step !== 'matching') return false;
  if (!isMultiplayerMatchType(state.type)) return false;
  return state.matchID == null;
}

/** 是否應在離開頁面時放棄配對 */
export function shouldAbandonOnLeave(state: Pick<GameFlowState, 'step' | 'type'>): boolean {
  return isActiveMatchPhase(deriveMatchPhaseFromState(state));
}

/** 瞬態 step：由 effects 處理，GameFlowUI 不渲染 */
export const TRANSIENT_MATCHMAKING_STEPS: ReadonlySet<GameFlowStep> = new Set([
  'inviteFriendToMatch',
  'waitingInviteID',
  'acceptFriendInvite',
  'rejectFriendInvite',
  'acceptFriendInviteAssignStockBattle',
  'receiveFriendReject',
  'receiveFriendInviteCancel',
  'sendRoomID',
]);

export function isTransientMatchmakingStep(step: GameFlowStep): boolean {
  return TRANSIENT_MATCHMAKING_STEPS.has(step);
}

/** 選股彈窗步驟：開啟時不顯示全域 loading，送出後才進入 loading */
export const STOCK_PICKER_STEPS: ReadonlySet<GameFlowStep> = new Set([
  'stockSelect',
  'multiAssignStockSelect',
  'friendBattleAssignStockSelect',
  'acceptFriendInviteAssignStockBattleSelectStock',
]);

export function isStockPickerStep(step: GameFlowStep): boolean {
  return STOCK_PICKER_STEPS.has(step);
}

/** 雙人隨機／自選：配對與倒數階段不顯示全域 loading，進遊戲前才顯示。 */
export function isMultiplayerPreGameStep(
  state: Pick<GameFlowState, 'type' | 'step'>,
): boolean {
  if (!isMultiplayerMatchType(state.type)) return false;
  return state.step === 'matching' || state.step === 'countdown';
}

/**
 * 是否應顯示 gameFlow 驅動的全域 loading（首頁 useBusyOverlay）。
 * 邀請／待回覆、選股、配對倒數等需使用者操作的階段不顯示。
 */
export function shouldShowGameFlowGlobalLoading(
  state: Pick<GameFlowState, 'step' | 'type'>,
): boolean {
  const { step, type } = state;
  if (step === 'idle') return false;
  if (isTransientMatchmakingStep(step)) return false;
  if (isStockPickerStep(step)) return false;
  if (isMultiplayerPreGameStep({ type, step })) return false;

  const phase = deriveMatchPhaseFromState({ step, type });
  if (phase === 'inviting' || phase === 'invited') return false;

  return true;
}
